import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Headline5
  from '../Typography/Heading/Headline5';
import SubTitle
  from '../Typography/SubTitle';
import SubTitle2,
{ Subtitle2Properties } from '../Typography/SubTitle2';
import Body2
  from '../Typography/Body/Body2';
import { List } from '.';
import breakpoints from '../Theme/breakpoints';
import Skeleton from '../SkeletonLoader/Skeleton';
import DescriptionSkeleton from '../SkeletonLoader/Default';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';
import rose from '../Palettes/rose';

const ListItem = styled.li<{ highlighted?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas: "logo jobDetails";
  gap: 16px;
  padding: 24px 0 0 0;
  position: relative;
  align-items: start;
  ${({ highlighted, theme }) => highlighted && `
  ::after {
    content: '';
    border-left: 3px solid ${theme.palette.primary.main};
    position: absolute;
    top: 24px;
    bottom: 30px;
    left: 4px;
  }
  `}
`;

export const ImageContainer = styled.div`
  grid-area: logo;
  width: 60px;
  height: 60px;
  display: grid;
  place-items: center;
  margin: 0 1rem;

  @media (min-width: ${breakpoints.xssm}px) {
    width: 100px;
    height: 100px;
  }

  /* stylelint-disable-next-line selector-type-no-unknown */
  progressive-img {
    display: grid;
    place-items: center;
  }

  && {
    /* stylelint-disable-next-line selector-type-no-unknown */
    progressive-img {
      --img-width: 60px;
      --img-object-fit: contain;
      --img-height: 80px;

      @media (min-width: ${breakpoints.xssm}px) {
        --img-width: 100px
      }
    }
  }
`;

const detailsContainerPropertyTypes = {
  showDivider: PropTypes.bool,
};

type DetailsContainerProperties = InferProps<
  typeof detailsContainerPropertyTypes
>;

const DetailsContainer = styled.div<DetailsContainerProperties>`
  grid-area: jobDetails;
  margin: 0;
  padding: 0 8px 8px 0;
  word-break: break-word;
  min-width: 0;
  overflow-wrap: break-word;
  border-bottom: ${({ showDivider }) => (
    showDivider ? `1px solid ${grey[325]}` : 'none'
  )};
`;

DetailsContainer.propTypes = detailsContainerPropertyTypes;

const StyledSubTitle = styled(SubTitle)`
  margin: .75rem 0;
  display: inline-block;
`;

const JobLocationSpan = styled(StyledSubTitle)`
  grid-area: jobLocation;
`;

const EBPLink = styled(StyledSubTitle)`
  a {
    color: ${rose[700]};
  }
  a:hover, a:active {
    text-decoration: underline;
  }
`;

const jobLocationPropertyTypes = {
  city: PropTypes.node,
  address: PropTypes.string,
  addressRegion: PropTypes.string,
  buildingName: PropTypes.string,
  postalCode: PropTypes.string,
  formattedDistance: PropTypes.string,
};

type JobLocationProperties = InferProps<
  typeof jobListItemPropertyTypes
>;

const JobLocation = ({
  buildingName,
  address,
  city,
  addressRegion,
  postalCode,
  formattedDistance,
}: JobLocationProperties) => (
  <>
    { buildingName && (
    <>
      {buildingName}
      {' '}
      |
      {' '}
    </>
    )}
    { address && (
      <>
        {address}
        {' '}
        |
        {' '}
      </>
    )}
    {city}
    { addressRegion && (
      <>
        {', '}
        {addressRegion}
      </>
    )}
    { postalCode && (
      <>
        {' '}
        {postalCode}
      </>
    )}
    { formattedDistance && (
      <>
        {' '}
        (
        {formattedDistance}
        )
      </>
    )}
  </>
);

JobLocation.propTypes = jobLocationPropertyTypes;

const PublishedAtContainer = styled(
  (properties: Subtitle2Properties) => <SubTitle2 {...properties} />,
)`
  grid-area: jobPublicationDate;
  min-width: 10em;
  margin: auto 0;

  @media (min-width: ${breakpoints.sm}px) {
    text-align: right;
  }
`;

const StyledBody2 = styled(Body2)`
  margin: 0;
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -webkit-box;
  -webkit-line-clamp: 5;
  /* autoprefixer: ignore next */
  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: ${breakpoints.xssm}px) {
    display: block;
    text-overflow: initial;
    -webkit-line-clamp: none;
    /* autoprefixer: ignore next */
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: initial;
    overflow: visible;
  }
`;

const DetailsFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "jobLocation jobPublicationDate"
    "jobLocation jobPublicationDate";
  gap: 8px;
  margin-bottom: 8px;
  align-items: start;

  @media (max-width: ${breakpoints.sm}px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "jobLocation"
      "jobPublicationDate";
  }
`;

const Badge = styled.span`
  background-color: ${({ theme }) => theme.palette.primary.main};
  padding: 1.5px 5px;
  margin-right: 5px;
  line-height: 1;
  letter-spacing: .01em;
  color: #fff;
  font-size: 14px;
  text-transform: uppercase;
  white-space: nowrap;
  position: absolute;
  top: 2px;

  &:first-of-type {
    left: 0;
  }

  &:nth-of-type(n+2) {
    left: 5rem;
  }
`;

const BadgesContainer = styled.span`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  justify-content: start;
  position: relative;
  height: 17px;
  min-width: 10rem;
  margin-top: 0.25rem;

  @media (min-width: ${breakpoints.sm}px) {
    display: inline-block;
  }
`;

const StyledHeadline5 = styled(Headline5)`
  margin-right: .25rem;
`;

const StyledSkeleton = styled(Skeleton)`
  display: inline-block;
`;

const jobListItemPropertyTypes = {
  title: PropTypes.node,
  teaser: PropTypes.node,
  company: PropTypes.node,
  city: PropTypes.node,
  address: PropTypes.string,
  addressRegion: PropTypes.string,
  buildingName: PropTypes.string,
  postalCode: PropTypes.string,
  formattedDistance: PropTypes.string,
  logo: PropTypes.node,
  badges: PropTypes.arrayOf(PropTypes.string),
  ebpHighlighted: PropTypes.bool,
  publishedAt: PropTypes.node,
  showDivider: PropTypes.bool,
  loadingBadges: PropTypes.bool,
  badgeLoader: PropTypes.node,
  showLoader: PropTypes.bool,
};

type JobListItemProperties = InferProps<
  typeof jobListItemPropertyTypes
> & {
  badges?: string[];
  ebpHighlighted?: boolean;
};

const JobListItem = ({
  title,
  teaser,
  company,
  city,
  address,
  addressRegion,
  buildingName,
  postalCode,
  formattedDistance,
  logo,
  badges,
  ebpHighlighted,
  publishedAt,
  showDivider,
  loadingBadges,
  badgeLoader,
  showLoader,
}: JobListItemProperties) => {
  const badgesValue = badges ?? [];
  const loadingBadgesValue = loadingBadges ?? false;
  const showLoaderValue = showLoader ?? false;

  return (
    <ListItem highlighted={ebpHighlighted}>
      {showLoaderValue ? (
        <ImageContainer>
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </ImageContainer>
      ) : <ImageContainer>{logo}</ImageContainer>}
      <DetailsContainer showDivider={showDivider ?? true}>
        {showLoaderValue ? (
          <StyledHeadline5>
            <StyledSkeleton
              variant="rectangular"
              width="50%"
            >
              &nbsp;
            </StyledSkeleton>
            <BadgesContainer>
              {!loadingBadgesValue && badgesValue.map((badge) => (
                <Skeleton
                  key={badge}
                  variant="rectangular"
                  width="40%"
                  height="100%"
                />
              ))}
              {badgeLoader}
            </BadgesContainer>
          </StyledHeadline5>
        ) : (
          <StyledHeadline5 tag="h2">
            {title}
            {' '}
            <BadgesContainer>
              {!loadingBadgesValue && badgesValue.map((badge) => (
                <Badge key={badge}>{badge}</Badge>
              ))}
              {badgeLoader}
            </BadgesContainer>
          </StyledHeadline5>
        )}
        <EBPLink tag="span">
          {showLoaderValue ? (
            <Skeleton variant="rectangular" width="100px">&nbsp;</Skeleton>
          ) : company}
        </EBPLink>
        <StyledBody2>
          {showLoaderValue ? <DescriptionSkeleton /> : teaser}
        </StyledBody2>
        <DetailsFooter>
          <JobLocationSpan tag="span">
            {showLoaderValue ? (
              <Skeleton variant="rectangular" width="100px">&nbsp;</Skeleton>
            ) : (
              <JobLocation
                city={city}
                address={address}
                addressRegion={addressRegion}
                buildingName={buildingName}
                postalCode={postalCode}
                formattedDistance={formattedDistance}
              />
            )}
          </JobLocationSpan>
          <PublishedAtContainer tag="span">
            {showLoaderValue
              ? <Skeleton variant="rectangular" width="100px">&nbsp;</Skeleton>
              : publishedAt}
          </PublishedAtContainer>
        </DetailsFooter>
      </DetailsContainer>
    </ListItem>
  );
};

JobListItem.propTypes = jobListItemPropertyTypes;

const relatedJobListItemPropertyTypes = {
  logo: PropTypes.node,
  title: PropTypes.node,
  company: PropTypes.node,
  buildingName: PropTypes.string,
  address: PropTypes.string,
  city: PropTypes.node,
  addressRegion: PropTypes.string,
  postalCode: PropTypes.string,
  formattedDistance: PropTypes.string,
  publishedAt: PropTypes.node,
};

type RelatedJobListItemProperties = InferProps<
  typeof relatedJobListItemPropertyTypes
>;

const RelatedJobListItemWrapper = styled.li`
  padding-top: 8px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas: "logo jobDetails";
  gap: 16px;
  align-items: start;
  border-top: 1px solid ${grey[325]};
`;

const RelatedJobListItemCompany = styled(SubTitle)`
  && {
    padding: 0;
    margin: 0;
  }
  display: inline-block;
  a {
    color: ${rose[700]};
  }
  a:hover, a:active {
    text-decoration: underline;
  }
`;

const RelatedJobListItemLocation = styled(SubTitle)`
  && {
    padding: 0;
    margin: 0;
  }
  grid-area: jobLocation;
`;

const RelatedJobListItemFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  grid-template-areas:
    "jobLocation jobPublicationDate"
    "jobLocation jobPublicationDate";
  align-items: start;

  @media (max-width: ${breakpoints.sm}px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "jobLocation"
      "jobPublicationDate";
  }
`;

const RelatedJobListItem = ({
  title,
  company,
  city,
  address,
  addressRegion,
  buildingName,
  postalCode,
  formattedDistance,
  logo,
  publishedAt,
}: RelatedJobListItemProperties) => (
  <RelatedJobListItemWrapper>
    <ImageContainer>{logo}</ImageContainer>
    <DetailsContainer>
      <StyledHeadline5 tag="h2">
        {title}
      </StyledHeadline5>
      <RelatedJobListItemCompany tag="span">
        {company}
      </RelatedJobListItemCompany>
      <RelatedJobListItemFooter>
        <RelatedJobListItemLocation tag="span">
          <JobLocation
            city={city}
            address={address}
            addressRegion={addressRegion}
            buildingName={buildingName}
            postalCode={postalCode}
            formattedDistance={formattedDistance}
          />
        </RelatedJobListItemLocation>
        <PublishedAtContainer tag="span">{publishedAt}</PublishedAtContainer>
      </RelatedJobListItemFooter>
    </DetailsContainer>
  </RelatedJobListItemWrapper>
);

RelatedJobListItem.propTypes = relatedJobListItemPropertyTypes;

const jobListPropertyTypes = {
  highlighted: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

type JobListProperties = InferProps<
  typeof jobListPropertyTypes
> & {
  className?: string;
};

const JobList = styled(
  ({ children, className }: JobListProperties) => (
    <List className={className}>{children}</List>
  ),
)<JobListProperties>`
  && {
    padding: 0;
  }
  background-color: ${
  ({ highlighted }) => ((highlighted ?? false) ? '#f5f5f5' : 'transparent')};
`;

JobList.propTypes = jobListPropertyTypes;

const JobListMessage = styled(Body2)`
  text-align: center;
  margin: 24px 0;
  padding: 8px;
  background-color: ${grey[325]};

  .keywords {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 600;
  }
`;

JobListMessage.defaultProps = {
  tag: 'div',
};

const CountContainer = styled.div`
  && {
    text-align: center;
    margin: 0 24px;
    color: ${overlay[600]};
    font-family: ${({ theme }) => theme.fonts.secondary};
    font-size: ${({ theme }) => theme.sizes.h3};
    line-height: ${({ theme }) => theme.sizes.h4};
  }
`;

const jobsCountPropertyTypes = {
  children: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

type JobsCountProperties = InferProps<typeof jobsCountPropertyTypes>;

const JobsCount = ({ children }: JobsCountProperties) => (
  <CountContainer>
    {children}
  </CountContainer>
);

JobsCount.propTypes = jobsCountPropertyTypes;

export {
  JobList, JobListItem,
  JobListMessage, JobsCount,
  RelatedJobListItem,
};
